import { useState, useEffect, Suspense } from 'react'
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { useTheme } from '../context/ThemeContext'
import NotificationBell from './NotificationBell'
import LanguageSwitcher from './LanguageSwitcher'
import useAdminLink from '../hooks/useAdminLink'

function getAdminPageTitle(path, t) {
  let title = ''
  if (path.startsWith('/admin/posts/new')) title += t('admin.newPostTitle')
  if (path?.match(/^\/admin\/posts\/edit/)) title += t('admin.editPostTitle')
  if (path === '/admin/posts') title += t('admin.postListTitle')
  if (path.startsWith('/admin/pages/new')) title += t('admin.newPageTitle')
  if (path?.match(/^\/admin\/pages\/edit/)) title += t('admin.editPageTitle')
  if (path === '/admin/pages') title += t('admin.pageListTitle')
  if (path === '/admin/blocks') title += t('admin.blockListTitle')
  if (path.startsWith('/admin/blocks/new')) title += t('admin.newBlockTitle')
  if (path?.match(/^\/admin\/blocks\/edit/)) title += t('admin.editBlockTitle')
  if (path === '/admin/slider') title += t('admin.sliderTitle')
  if (path.startsWith('/admin/slider/new')) title += t('admin.newSlideTitle')
  if (path?.match(/^\/admin\/slider\/edit/)) title += t('admin.editSlideTitle')
  if (path.startsWith('/admin/menus')) title += t('admin.menusTitle')
  if (path === '/admin/menus/principal') title += ' - ' + t('admin.principal')
  if (path === '/admin/menus/social') title += ' - ' + t('admin.social')
  if (path === '/admin/menus/footer') title += ' - ' + t('admin.footer')
  if (path === '/admin/forms') title += t('admin.formsTitle')
  if (path === '/admin/sends') title += t('admin.sendsTitle')
  if (path?.match(/^\/admin\/sends\/[^/]+$/)) title += t('admin.sendDetailTitle')
  if (path === '/admin/categories') title += t('admin.categoriesTitle')
  if (path.startsWith('/admin/categories/new')) title += t('admin.newCategoryTitle')
  if (path?.match(/^\/admin\/categories\/edit/)) title += t('admin.editCategoryTitle')
  if (path === '/admin/products') title += t('admin.productListTitle')
  if (path.startsWith('/admin/products/new')) title += t('admin.newProductTitle')
  if (path?.match(/^\/admin\/products\/edit/)) title += t('admin.editProductTitle')
  if (path === '/admin/products/categories') title += t('admin.productCategoriesTitle')
  if (path.startsWith('/admin/products/categories/new')) title += t('admin.newProductCategoryTitle')
  if (path?.match(/^\/admin\/products\/categories\/edit/)) title += t('admin.editProductCategoryTitle')
  if (path === '/admin/orders') title += t('admin.orderListTitle')
  if (path.startsWith('/admin/orders/')) title += t('admin.orderDetailTitle')
  if (path.startsWith('/admin/subscriptions')) title += t('admin.subscriptionListTitle')
  if (path === '/admin/email-queue') title += t('admin.emailQueueTitle')
  if (path === '/admin/email-templates') title += t('admin.emailTemplatesTitle')
  if (path.startsWith('/admin/email-templates/edit')) title += t('admin.editEmailTemplateTitle')
  if (path === '/admin/email-settings') title += t('admin.emailSettingsTitle')
  if (path.startsWith('/admin/payment-methods')) title += t('admin.paymentMethodsTitle')
  if (path === '/admin/notifications') title += t('admin.notificationsTitle')
  if (path === '/admin/chat') title += t('admin.chatsTitle')
  if (path === '/admin/chat/settings') title += t('admin.chatSettingsTitle')
  if (path === '/admin/tickets') title += t('admin.supportListTitle')
  if (path?.match(/^\/admin\/tickets\/(?!new)/)) title += t('admin.supportDetailTitle')
  if (path === '/admin/subscribers') title += t('newsletter.adminTitle')
  if (path.startsWith('/admin/newsletter/campaigns')) title += t('admin.campaignsTitle')
  if (path.startsWith('/admin/newsletter/templates')) title += t('admin.newsletterTemplatesTitle')
  if (path.startsWith('/admin/users')) title += t('admin.usersTitle')
  if (path.startsWith('/admin/api-keys')) title += t('admin.apiKeysTitle')
  if (path === '/admin') title += t('admin.dashboardTitle')
  if (path !== '/admin' && !path.startsWith('/admin/posts') && !path.startsWith('/admin/pages') && !path.startsWith('/admin/blocks') && !path.startsWith('/admin/slider') && !path.startsWith('/admin/menus') && !path.startsWith('/admin/categories') && !path.startsWith('/admin/products') && !path.startsWith('/admin/orders') && !path.startsWith('/admin/subscriptions') && !path.startsWith('/admin/email') && !path.startsWith('/admin/payment') && !path.startsWith('/admin/forms') && !path.startsWith('/admin/sends') && !path.startsWith('/admin/notifications') && !path.startsWith('/admin/chat') && !path.startsWith('/admin/tickets') && !path.startsWith('/admin/subscribers') && !path.startsWith('/admin/newsletter') && !path.startsWith('/admin/users') && !path.startsWith('/admin/api-keys') && t('admin.dashboardTitle')) title += t('admin.dashboardTitle')
  return title
}

const menuItems = [
  {
    label: 'Dashboard',
    labelKey: 'admin.dashboard',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </svg>
    ),
    path: '/admin',
  },
  {
    label: 'Posts',
    labelKey: 'admin.posts',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    ),
    children: [
      { label: 'Posts', labelKey: 'admin.posts', path: '/admin/posts' },
      { label: 'Categories', labelKey: 'admin.categories', path: '/admin/categories' },
    ],
  },
  {
    label: 'Ecommerce',
    labelKey: 'admin.ecommerce',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1"></circle>
        <circle cx="20" cy="21" r="1"></circle>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
      </svg>
    ),
    children: [
      { label: 'Products', labelKey: 'admin.products', path: '/admin/products' },
      { label: 'Product Categories', labelKey: 'admin.productCategories', path: '/admin/products/categories' },
      { label: 'Orders', labelKey: 'admin.orders', path: '/admin/orders', adminOnly: true },
      { label: 'Subscriptions', labelKey: 'admin.subscriptions', path: '/admin/subscriptions', adminOnly: true },
      { label: 'Payment Methods', labelKey: 'admin.paymentMethods', path: '/admin/payment-methods', adminOnly: true },
    ],
  },
  {
    label: 'Pages',
    labelKey: 'admin.pages',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
        <polyline points="13 2 13 9 20 9"></polyline>
      </svg>
    ),
    path: '/admin/pages',
  },
  {
    label: 'Blocks',
    labelKey: 'admin.blocks',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
      </svg>
    ),
    path: '/admin/blocks',
  },
  {
    label: 'Menus',
    labelKey: 'admin.menus',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6"></line>
        <line x1="8" y1="12" x2="21" y2="12"></line>
        <line x1="8" y1="18" x2="21" y2="18"></line>
        <line x1="3" y1="6" x2="3.01" y2="6"></line>
        <line x1="3" y1="12" x2="3.01" y2="12"></line>
        <line x1="3" y1="18" x2="3.01" y2="18"></line>
      </svg>
    ),
    children: [
      { label: 'Principal', labelKey: 'admin.principal', path: '/admin/menus/principal' },
      { label: 'Social', labelKey: 'admin.social', path: '/admin/menus/social' },
      { label: 'Footer', labelKey: 'admin.footer', path: '/admin/menus/footer' },
    ],
  },
  {
    label: 'Web Forms',
    labelKey: 'admin.webForms',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    ),
    children: [
      { label: 'Forms', labelKey: 'admin.forms', path: '/admin/forms' },
      { label: 'Sends', labelKey: 'admin.sends', path: '/admin/sends' },
    ],
  },
  {
    label: 'Slider',
    labelKey: 'admin.slider',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
        <line x1="8" y1="21" x2="16" y2="21"></line>
        <line x1="12" y1="17" x2="12" y2="21"></line>
      </svg>
    ),
    path: '/admin/slider',
    adminOnly: true,
  },
  {
    label: 'Users',
    labelKey: 'admin.users',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
    path: '/admin/users',
    adminOnly: true,
  },
  {
    label: 'API Keys',
    labelKey: 'admin.apiKeys',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
      </svg>
    ),
    path: '/admin/api-keys',
    adminOnly: true,
  },
  {
    label: 'Live Chat',
    labelKey: 'admin.liveChat',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
    ),
    children: [
      { label: 'Chats', labelKey: 'admin.chats', path: '/admin/chat' },
      { label: 'Settings', labelKey: 'admin.chatSettings', path: '/admin/chat/settings' },
    ],
  },
  {
    label: 'Support',
    labelKey: 'admin.support',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    ),
    path: '/admin/tickets',
  },
  {
    label: 'Email',
    labelKey: 'admin.email',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
      </svg>
    ),
    children: [
      { label: 'Templates', labelKey: 'admin.emailTemplates', path: '/admin/email-templates' },
      { label: 'Queue', labelKey: 'admin.queue', path: '/admin/email-queue' },
      { label: 'Settings', labelKey: 'admin.emailSettings', path: '/admin/email-settings' },
      { label: 'Subscribers', labelKey: 'admin.subscribers', path: '/admin/subscribers' },
      { label: 'Campaigns', labelKey: 'admin.campaigns', path: '/admin/newsletter/campaigns' },
      { label: 'Newsletter Templates', labelKey: 'admin.newsletterTemplates', path: '/admin/newsletter/templates' },
    ],
    adminOnly: true,
  },
]

export default function AdminLayout() {
  const { t } = useTranslation()
  const { isAuthenticated, loading, isAdmin, isEditor } = useAuth()
  const { theme, toggleTheme, isDark } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const { lang } = useParams()
  const adminLink = useAdminLink()
  const normalizedPath = lang ? location.pathname.replace(`/${lang}`, '') : location.pathname

  // Sync i18next language with URL param
  useEffect(() => {
    if (lang && ['en', 'es'].includes(lang)) {
      i18n.changeLanguage(lang)
    } else {
      i18n.changeLanguage('en')
    }
  }, [lang])

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [openGroups, setOpenGroups] = useState({})
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const closeSidebar = () => {
    if (isMobile) setMobileSidebarOpen(false)
  }

  // Auto-open groups based on current route
  useEffect(() => {
    const newOpenGroups = {}
    menuItems.forEach(item => {
      if (item.children) {
        const isActive = item.children.some(c => normalizedPath.startsWith(c.path))
        newOpenGroups[item.label] = isActive
      }
    })
    setOpenGroups(prev => ({ ...prev, ...newOpenGroups }))
  }, [normalizedPath])

  const toggleGroup = (label) => {
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }))
  }

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      window.location.href = '/login'
    }
  }, [loading, isAuthenticated])

  useEffect(() => {
    if (!loading && isAuthenticated && !isAdmin && !isEditor) {
      navigate('/', { replace: true })
    }
  }, [loading, isAuthenticated, isAdmin, isEditor, navigate])

  if (loading) {
    return <div className="loading"></div>
  }

  const isAdminPage = normalizedPath.startsWith('/admin')
  if (!isAdminPage) {
    return <Outlet />
  }

  const pageTitle = getAdminPageTitle(normalizedPath, t)

  return (
    <div className="admin-layout">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
        <div className="admin-sidebar-header">
          <Link to={adminLink('/')} className="admin-logo" onClick={closeSidebar}>
            <span className="admin-logo-icon">JN</span>
            {!sidebarCollapsed && <span className="admin-logo-text">Admin</span>}
          </Link>
          <button
            className="sidebar-toggle"
            onClick={() => {
              if (isMobile) {
                setMobileSidebarOpen(false)
              } else {
                setSidebarCollapsed(!sidebarCollapsed)
              }
            }}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        <nav className="admin-nav">
          {menuItems.filter(item => {
            if (item.adminOnly) return isAdmin
            return isEditor
          }).map((item) => {
            const hasChildren = item.children && item.children.length > 0
            const isGroupOpen = hasChildren && openGroups[item.label]
            const isParentActive = hasChildren && item.children.some(c => normalizedPath.startsWith(c.path))

            return (
              <div key={item.path || item.label} className={`admin-nav-group ${isGroupOpen || isParentActive ? 'open' : ''}`}>
                {hasChildren ? (
                  <>
                    <button
                      type="button"
                      className={`admin-nav-item admin-nav-group-toggle ${isParentActive ? 'active' : ''}`}
                      onClick={() => toggleGroup(item.label)}
                    >
                      <span className="admin-nav-icon">{item.icon}</span>
                      {!sidebarCollapsed && (
                        <>
                          <span className="admin-nav-label">{t(item.labelKey || item.label)}</span>
                          <svg className={`admin-nav-arrow ${isGroupOpen || isParentActive ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </>
                      )}
                    </button>
                    {!sidebarCollapsed && (isGroupOpen || isParentActive) && (
                      <div className="admin-nav-children">
                        {item.children.filter(child => !child.adminOnly || isAdmin).map(child => (
                          <Link
                            key={child.path}
                            to={adminLink(child.path)}
                            className={`admin-nav-child ${normalizedPath === child.path ? 'active' : ''}`}
                            onClick={closeSidebar}
                          >
                            {t(child.labelKey || child.label)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={adminLink(item.path)}
                    className={`admin-nav-item ${(item.path === '/admin' ? normalizedPath === '/admin' : normalizedPath.startsWith(item.path) || isParentActive) ? 'active' : ''}`}
                    onClick={closeSidebar}
                  >
                    <span className="admin-nav-icon">{item.icon}</span>
                    {!sidebarCollapsed && (
                      <span className="admin-nav-label">{t(item.labelKey || item.label)}</span>
                    )}
                  </Link>
                )}
              </div>
            )
          })}
        </nav>
      </aside>

      {/* Mobile overlay */}
      <div
        className={`admin-sidebar-overlay ${mobileSidebarOpen ? 'open' : ''}`}
        onClick={() => setMobileSidebarOpen(false)}
      />

      {/* Main content */}
      <main className="admin-main">
        <header className="admin-header">
          <div className="admin-header-title">
            <button
              className="admin-mobile-toggle"
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              aria-label="Toggle sidebar"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <h1>{pageTitle}</h1>
          </div>
          <div className="admin-header-actions">
            <LanguageSwitcher />
            <NotificationBell />
            <button
              onClick={toggleTheme}
              className="btn btn-outline btn-sm"
              title={isDark ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
            >
              {isDark ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
          </div>
        </header>
        <div className="admin-content">
          <Suspense fallback={<div className="loading"></div>}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  )
}
